import { store } from 'app/shared/store/store';
import { NbnProductsResponse, NbnAddressResponse, NbnAddressErrorResponse } from 'app/nbn/models/nbn-products-response.model';
import {
  NbnServiceabilityFail,
  ProductCatalogueProduct,
  ProductCategory,
  ProductsResponse,
  ProductSearchRequestModel,
  ReportErrorType,
  ResponseError
} from 'app/shared/models/index';
import { errorReportingService } from 'app/shared/services/error-reporting/error-reporting.service';
import { abortControllerService, AbortControllerRequest } from 'app/shared/services/abort-controller/abort-controller.service';
import { getHeaders } from 'app/shared/utilities/helper-functions';

class NbnProductSearchApiService {
  public getProducts = async (request: ProductSearchRequestModel): Promise<NbnProductsResponse> => {
    abortControllerService.abortPendingGetProductsRequests('NbnProductSearchApiService.getProducts');
    const abortControllerRequest: AbortControllerRequest = abortControllerService.trackRequest('NbnProductSearchApiService.getProducts');

    const configData = store.get('configData');

    const init = {
      signal: abortControllerRequest.controller.signal,
      method: 'POST',
      body: JSON.stringify({
        ...request
      }),
      headers: getHeaders()
    };

    const searchEndpoint = `${configData.bffApiUrl}/products/nbn/search`;

    return await fetch(searchEndpoint, init)
      .then((response) => {
        return this.processProductsSearchResponse(response);
      })
      .catch((error) => {
        return this.processProductSearchError(error);
      })
      .finally(() => {
        abortControllerRequest.isPending = false;
      });
  };

  public getProductsByLocationId = async (locationId: string): Promise<NbnProductsResponse> => {
    abortControllerService.abortPendingGetProductsRequests('NbnProductSearchApiService.getProductsByLocationId');
    const abortControllerRequest: AbortControllerRequest = abortControllerService.trackRequest(
      'NbnProductSearchApiService.getProductsByLocationId'
    );
    const request = { locationId };
    const configData = store.get('configData');

    const init = {
      signal: abortControllerRequest.controller.signal,
      method: 'POST',
      body: JSON.stringify({
        ...request
      }),
      headers: getHeaders()
    };

    const searchEndpoint = `${configData.bffApiUrl}/products/nbn/search/locationId`;

    return await fetch(searchEndpoint, init)
      .then((response) => {
        return this.processProductsSearchResponse(response);
      })
      .catch((error) => {
        return this.processProductSearchError(error);
      })
      .finally(() => {
        abortControllerRequest.isPending = false;
      });
  };

  private processProductsSearchResponse = (response: Response): Promise<NbnProductsResponse> => {
    if (!response.ok) {
      if (response.status === 300) {
        errorReportingService.logError(NbnServiceabilityFail.multipleMatchingAddresses, ReportErrorType.NbnServiceabilityFailed);
        throw new ResponseError(response.statusText, 'multipleMatching');
      } else if (response.status === 500) {
        errorReportingService.logError(NbnServiceabilityFail.addressUnserviceable, ReportErrorType.NbnServiceabilityFailed);
        throw new ResponseError(response.statusText, 'systemError');
      } else {
        errorReportingService.logError(NbnServiceabilityFail.unableToRetrieveNbnProducts, ReportErrorType.NbnServiceabilityFailed);
        throw new ResponseError(`Unable to retrieve nbn products`, 'serviceNotAvailable');
      }
    }
    return response.json();
  };

  private processProductSearchError = (error: any) => {
    let responseError: ResponseError;
    if (error.errorType) {
      responseError = new ResponseError(error.message, error.errorType);
    } else {
      errorReportingService.logError(NbnServiceabilityFail.failedToCompleteHttpRequest, ReportErrorType.NbnServiceabilityFailed);
      responseError = new ResponseError(error);
    }
    const isAborted: boolean = typeof error === 'string' ? false : error?.name === 'AbortError';
    if (isAborted) {
      responseError.errorType = 'aborted';
    }
    throw responseError;
  };

  private getProductByProductCategory = async (productCategory: ProductCategory, state?: string): Promise<ProductsResponse> => {
    const configData = store.get('configData');
    const httpHeaders = getHeaders();
    let requestUrl = `${configData.bffApiUrl}/products?productCategory=${productCategory}`;
    if (state) {
      requestUrl = `${requestUrl}&state=${state}`;
    }

    try {
      const response = await fetch(requestUrl, {
        method: 'GET',
        headers: httpHeaders
      });

      const data = response.json();
      if (!response.ok) {
        throw new ResponseError(`Unable to retrieve voip products`, 'serviceNotAvailable');
      }

      return data;
    } catch (error) {
      if (error.errorType) {
        throw new ResponseError(error.message, error.errorType);
      } else {
        throw new ResponseError(error);
      }
    }
  };

  public getVoipProduct = async (state?: string): Promise<ProductsResponse> => {
    return await this.getProductByProductCategory('voip', state);
  };

  public getModemProduct = async (state?: string): Promise<ProductsResponse> => {
    return await this.getProductByProductCategory('modem', state);
  };

  public getNbnAddress = async (nbnLocationId: string): Promise<NbnAddressResponse> => {
    const configData = store.get('configData');
    const httpHeaders = getHeaders();
    const requestUrl = `${configData.bffApiUrl}/nbn/location?locationId=${nbnLocationId}`;
    try {
      const response = await fetch(requestUrl, {
        method: 'GET',
        headers: httpHeaders
      });
      return await response.json();
    } catch (e) {
      const error: NbnAddressErrorResponse = { code: 'NetworkError', message: e.message };
      return error;
    }
  };

  public getNbnProduct = async (sku: string): Promise<ProductCatalogueProduct> => {
    const configData = store.get('configData');
    if (configData) {
      const httpHeaders = getHeaders();
      const requestUrl = `${configData.bffApiUrl}/products/nbn/sku/${sku}`;

      try {
        const response = await fetch(requestUrl, {
          method: 'GET',
          headers: httpHeaders
        });
        return await response.json();
      } catch (e) {
        const error: NbnAddressErrorResponse = { code: 'NetworkError', message: e.message };
        throw error;
      }
    }
  };
}

export const nbnProductSearchApiService = new NbnProductSearchApiService();
